import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";

type ClerkSession = {
	getToken: () => Promise<string | null>;
};

type BrowserClerk = {
	session?: ClerkSession | null;
	signOut?: () => Promise<void>;
	redirectToSignIn?: () => Promise<void>;
};

declare global {
	interface Window {
		Clerk?: BrowserClerk;
	}
}

function browserClerk(): BrowserClerk | undefined {
	if (typeof window === "undefined") {
		return undefined;
	}

	return window.Clerk;
}

async function attachBearerToken(
	config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> {
	const token = await browserClerk()?.session?.getToken();

	if (token) {
		config.headers.set("Authorization", `Bearer ${token}`);
	}

	return config;
}

async function redirectToSignIn(): Promise<void> {
	const clerk = browserClerk();
	if (clerk?.signOut) {
		await clerk.signOut();
	}
	if (clerk?.redirectToSignIn) {
		await clerk.redirectToSignIn();
		return;
	}

	window.location.href = "/sign-in";
}

export function apiClient(): AxiosInstance {
	const instance = axios.create({
		baseURL: process.env.REACT_APP_API_URL || "http://localhost:3001/api",
		timeout: 10000,
		headers: {
			"Content-Type": "application/json",
			Accept: "application/json",
		},
	});

	instance.interceptors.request.use(attachBearerToken);

	instance.interceptors.response.use(
		(response) => response,
		async (error) => {
			if (error.response?.status === 401) {
				try {
					await redirectToSignIn();
				} catch (clerkError) {
					console.error("Failed to redirect to sign-in:", clerkError);
					window.location.href = "/sign-in";
				}
			}

			if (error.response?.status === 307) {
				window.location.href = "/start";
			}
			return Promise.reject(error);
		},
	);

	return instance;
}
